<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-setting" title="调研题目配置" subtitle="配置员工离职调研问卷的题目内容，支持导入导出" />

    <!-- 工具栏 -->
    <div class="toolbar">
      <el-input v-model="search" placeholder="搜索题目关键词" prefix-icon="el-icon-search" clearable style="width: 260px;" />
      <div style="margin-left: auto; display: flex; gap: 12px;">
        <el-button icon="el-icon-download" class="btn-click" @click="downloadTemplate">下载模板</el-button>
        <el-button icon="el-icon-upload2" class="btn-click" @click="openImport">导入题目</el-button>
        <el-button icon="el-icon-download" class="btn-click" @click="exportQuestions">导出题目</el-button>
        <el-button type="primary" icon="el-icon-plus" class="btn-click" @click="openDialog()">新增题目</el-button>
      </div>
    </div>

    <!-- 表格 -->
    <div class="table-wrapper">
      <el-table :data="pagedQuestions" stripe class="data-table">
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column prop="question_text" label="题目内容" min-width="240" show-overflow-tooltip />
        <el-table-column label="题型" width="100">
          <template slot-scope="scope">
            <el-tag size="mini" :type="typeTagType(scope.row.question_type)">{{ typeLabel(scope.row.question_type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="是否必填" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.required ? 'success' : 'info'" size="mini">{{ scope.row.required ? '是' : '否' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sort_order" label="排序" width="70" />
        <el-table-column label="选项预览" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.options && scope.row.options.length">{{ scope.row.options.join('、') }}</span>
            <span v-else style="color: var(--color-text-muted);">—</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120">
          <template slot-scope="scope">
            <el-button type="text" class="action-edit" @click="openDialog(scope.row)">编辑</el-button>
            <el-button type="text" class="action-disable" @click="remove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="filteredQuestions.length"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[10, 20, 50]"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialog.isEdit ? '编辑题目' : '新增题目'" :visible.sync="dialog.visible" width="560px" :append-to-body="true">
      <el-form :model="dialog.form" label-width="100px">
        <el-form-item label="题目内容" required>
          <el-input type="textarea" v-model="dialog.form.question_text" :rows="2" placeholder="输入题目内容" />
        </el-form-item>
        <el-form-item label="题目类型" required>
          <el-select v-model="dialog.form.question_type" placeholder="选择题型" style="width: 100%;" @change="onTypeChange">
            <el-option label="单选题" value="single_choice" />
            <el-option label="多选题" value="multiple_choice" />
            <el-option label="填空题" value="fill_blank" />
          </el-select>
        </el-form-item>
        <el-form-item label="是否必填">
          <el-switch v-model="dialog.form.required" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="排序序号">
          <el-input-number v-model="dialog.form.sort_order" :min="0" />
        </el-form-item>

        <!-- 选择题特有 -->
        <template v-if="dialog.form.question_type !== 'fill_blank'">
          <el-form-item label="最多选几项" v-if="dialog.form.question_type === 'multiple_choice'">
            <el-input-number v-model="dialog.form.max_select" :min="1" placeholder="不填则无限制" />
          </el-form-item>
          <el-form-item label="选项列表" required>
            <div v-for="(opt, i) in dialog.form.options" :key="i" class="option-row">
              <el-input v-model="dialog.form.options[i]" size="small" placeholder="选项内容" style="flex: 1;" />
              <el-button type="text" style="color: var(--color-high);" @click="removeOption(i)">删除</el-button>
            </div>
            <el-button type="text" icon="el-icon-plus" @click="addOption">添加选项</el-button>
          </el-form-item>
        </template>
      </el-form>
      <span slot="footer">
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" @click="save">保存</el-button>
      </span>
    </el-dialog>

    <!-- 导入弹窗 -->
    <el-dialog title="导入题目" :visible.sync="importVisible" width="560px" :append-to-body="true">
      <div class="import-area">
        <p class="import-tip">
          <i class="el-icon-info" style="color: var(--color-primary);"></i>
          请上传 CSV 文件，格式：题目内容,题型,是否必填,排序,最多选几项,选项1,选项2...
        </p>
        <el-upload
          ref="uploadRef"
          action=""
          :auto-upload="false"
          :on-change="handleFileChange"
          :limit="1"
          accept=".csv"
          drag
          class="upload-box"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        </el-upload>
      </div>
      <span slot="footer">
        <el-button @click="importVisible = false">关闭</el-button>
        <el-button type="primary" @click="confirmImport">确认导入</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'

export default {
  name: 'FeedbackSurveyManage',
  components: { PageHeader },
  data() {
    return {
      search: '',
      page: 1,
      pageSize: 10,
      dialog: {
        visible: false,
        isEdit: false,
        form: {
          question_id: null,
          question_text: '',
          question_type: 'single_choice',
          options: [],
          required: 1,
          sort_order: 0,
          max_select: null
        }
      },
      importVisible: false,
      importContent: ''
    }
  },
  computed: {
    questions() {
      return this.$store.state.surveys.questions || []
    },
    filteredQuestions() {
      let data = [...this.questions]
      if (this.search) {
        const k = this.search.toLowerCase()
        data = data.filter(q => (q.question_text || '').toLowerCase().includes(k))
      }
      return data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    },
    pagedQuestions() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredQuestions.slice(start, start + this.pageSize)
    }
  },
  created() {
    this.loadQuestions()
  },
  methods: {
    async loadQuestions() {
      try {
        const res = await this.$api.fetchSurveyQuestions()
        if (res.data && res.data.code === 200) {
          this.$store.commit('SET_SURVEY_QUESTIONS', res.data.data || [])
        }
      } catch (e) {
        console.error('加载题目失败', e)
      }
    },
    typeLabel(type) {
      const map = { single_choice: '单选题', multiple_choice: '多选题', fill_blank: '填空题' }
      return map[type] || type
    },
    typeTagType(type) {
      const map = { single_choice: 'primary', multiple_choice: 'warning', fill_blank: 'info' }
      return map[type] || ''
    },
    handleSizeChange(size) { this.pageSize = size; this.page = 1 },
    handlePageChange(page) { this.page = page },
    openDialog(row) {
      if (row) {
        this.dialog.isEdit = true
        this.dialog.form = {
          question_id: row.question_id,
          question_text: row.question_text,
          question_type: row.question_type,
          options: [...(row.options || [])],
          required: row.required,
          sort_order: row.sort_order || 0,
          max_select: row.max_select || null
        }
      } else {
        this.dialog.isEdit = false
        this.dialog.form = {
          question_id: null,
          question_text: '',
          question_type: 'single_choice',
          options: [''],
          required: 1,
          sort_order: this.questions.length + 1,
          max_select: null
        }
      }
      this.dialog.visible = true
    },
    onTypeChange() {
      if (this.dialog.form.question_type === 'fill_blank') {
        this.dialog.form.options = []
        this.dialog.form.max_select = null
      } else if (!this.dialog.form.options.length) {
        this.dialog.form.options = ['']
      }
    },
    addOption() {
      this.dialog.form.options.push('')
    },
    removeOption(i) {
      this.dialog.form.options.splice(i, 1)
    },
    async save() {
      const form = this.dialog.form
      if (!form.question_text.trim()) {
        this.$message.error('题目内容不能为空')
        return
      }
      if (form.question_type !== 'fill_blank' && form.options.filter(o => o.trim()).length === 0) {
        this.$message.error('选择题至少需要一个选项')
        return
      }
      // 过滤空选项
      form.options = form.options.filter(o => o.trim())
      try {
        if (this.dialog.isEdit) {
          await this.$api.updateSurveyQuestion(form.question_id, form)
          this.$store.commit('UPDATE_SURVEY_QUESTION', { ...form })
          this.$message.success('题目已更新')
        } else {
          const res = await this.$api.createSurveyQuestion(form)
          this.$store.commit('ADD_SURVEY_QUESTION', { ...form, question_id: res.data.data.question_id })
          this.$message.success('题目已新增')
        }
        this.dialog.visible = false
      } catch (e) {
        this.$message.error('保存失败')
      }
    },
    async remove(row) {
      try {
        await this.$confirm('确定删除此题目？', '提示', { type: 'warning' })
        await this.$api.deleteSurveyQuestion(row.question_id)
        this.$store.commit('DELETE_SURVEY_QUESTION', row.question_id)
        this.$message.success('已删除')
      } catch (e) {}
    },
    downloadTemplate() {
      const headers = '题目内容,题型,是否必填,排序,最多选几项,选项1,选项2,选项3\n'
      const sample = '您考虑离职的主要原因是什么？,multiple_choice,是,1,3,薪酬福利不符合预期,个人职业发展空间受限,寻找到了更好的外部职业机会\n'
      const blob = new Blob([headers + sample], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '调研题目导入模板.csv'
      link.click()
    },
    async exportQuestions() {
      try {
        const res = await this.$api.exportSurveyQuestions()
        if (res.data && res.data.code === 200) {
          const csv = res.data.data.csv_content
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = res.data.data.filename
          link.click()
        }
      } catch (e) {
        this.$message.error('导出失败')
      }
    },
    openImport() {
      this.importVisible = true
      this.importContent = ''
      this.$nextTick(() => {
        if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
      })
    },
    handleFileChange(file) {
      if (!file.raw) return
      const reader = new FileReader()
      reader.onload = (e) => {
        this.importContent = e.target.result
      }
      reader.readAsText(file.raw)
    },
    async confirmImport() {
      if (!this.importContent) {
        this.$message.warning('请先上传文件')
        return
      }
      try {
        const res = await this.$api.importSurveyQuestions({ csv_content: this.importContent })
        if (res.data && res.data.code === 200) {
          this.$message.success(`导入成功 ${res.data.data.imported_count} 条`)
          this.loadQuestions()
          this.importVisible = false
        }
      } catch (e) {
        this.$message.error('导入失败')
      }
    }
  }
}
</script>

<style scoped>
.page-container { max-width: 1200px; margin: 0 auto; }
.toolbar { display: flex; align-items: center; margin-bottom: var(--space-lg); flex-wrap: wrap; gap: 8px; }
.table-wrapper { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.data-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }
.pagination-bar { display: flex; justify-content: flex-end; padding: var(--space-md) var(--space-lg); background: var(--color-surface); border-top: 1px solid var(--color-border); }
.action-edit { color: var(--color-primary) !important; }
.action-disable { color: var(--color-high) !important; }

.option-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }

.import-area { padding: 0 10px; }
.import-tip { font-size: 13px; color: var(--color-text-secondary); margin-bottom: 16px; line-height: 1.6; }
.upload-box >>> .el-upload { width: 100%; }
.upload-box >>> .el-upload-dragger { width: 100%; }
</style>
